import { useEffect, useRef } from "react";

type Props = {
  open: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  /** Si true, click fuera del contenido cierra el modal. */
  closeOnBackdrop?: boolean;
  /** Id del elemento que titula el modal (h1/h2/etc dentro de children).
   *  Si está, se pasa a aria-labelledby para que los lectores de pantalla
   *  anuncien el título en lugar de "dialog" genérico. */
  labelledBy?: string;
  /** Clase Tailwind de ancho máximo del contenedor. Default max-w-lg.
   *  Permite que distintos modales (perfil chico, ayuda ancho) reusen el
   *  mismo Modal con su a11y sin clonar el overlay. */
  maxWidthClass?: string;
};

// Selector de elementos focusables — cubre los casos típicos del proyecto
// (botones, links, inputs/textareas, elementos con tabindex explícito).
// :not([disabled]) y :not([tabindex='-1']) excluyen los que el browser
// igual saltearía.
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  onClose,
  children,
  closeOnBackdrop = false,
  labelledBy,
  maxWidthClass = "max-w-lg",
}: Props) {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Bloquear scroll del body cuando está abierto.
  useEffect(() => {
    if (!open) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = orig;
    };
  }, [open]);

  // A11y: Esc cierra, foco inicial al primer focusable interno, restore
  // del activeElement al cerrar. Esto evita que el lector de pantalla
  // anuncie "dialog" pero el foco quede afuera del contenido (lo que
  // confunde al usuario porque no sabe dónde está parado).
  useEffect(() => {
    if (!open) return;

    const previousActive = document.activeElement as HTMLElement | null;
    const node = dialogRef.current;
    if (node) {
      const first = node.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      // Si no hay focusables, focusear el contenedor mismo (tabIndex=-1)
      // para que Tab arranque desde adentro, no desde el body.
      (first ?? node).focus();
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && onClose) {
        e.preventDefault();
        onClose();
        return;
      }
      // Focus trap: ciclar Tab entre primer y último focusable. Sin esto
      // Tab se va al body de la página de fondo, contradiciendo el
      // aria-modal que le promete al lector de pantalla que solo el
      // dialog es navegable.
      if (e.key === "Tab" && node) {
        const focusables = Array.from(
          node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        );
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      // Restore foco al elemento que abrió el modal — patrón estándar
      // a11y. Sin esto el foco queda en el body después de cerrar.
      previousActive?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      // role="dialog" + aria-modal="true" — accesibilidad (lectores de
      // pantalla saben que es un modal) y tests E2E lo localizan con
      // page.getByRole("dialog").
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      ref={dialogRef}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm outline-none"
      onClick={(e) => {
        if (closeOnBackdrop && onClose && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={`max-h-[90vh] w-full ${maxWidthClass} overflow-y-auto rounded-2xl bg-[#1a1540] p-6 shadow-2xl`}
      >
        {children}
      </div>
    </div>
  );
}
